import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./index.css";

// Import all slider images
import hyteX50 from "../assets/hyte-x50-hero.webp";
import enthooPro2 from "../assets/enthoo-pro-2-hero.webp";
import explosivePerformance from "../assets/explosive-performance-hero.webp";
import ap202Hero from "../assets/ap202-hero.webp";
import heroCommunity from "../assets/hero-community.webp";
import amdRyzenHero from "../assets/amd-ryzen-hero.avif";
import nvidia5090Hero from "../assets/nvidia-5090-hero.avif";

const slides = [
  {
    id: 1,
    image: hyteX50,
    bgPosition: "center center",
    textBoundary: 40, // Percentage from left where text should stop
    title: '<span style="color: #DE3431">Check Out:</span> HYTE X50',
    subtitle: "Pick Your Color",
    description: "The HYTE X50 is available how you want it. Select a ready to play system or build yours from the ground up.",
    buttons: [
      { text: "Build Your Own", href: "https://arsenalpc.com/product/custom-pc/hyte-x50-custom-gaming-pc/" }
    ]
  },
  {
    id: 2,
    image: enthooPro2,
    bgPosition: "center center",
    textBoundary: 45, // Percentage from left where text should stop
    title: 'New <span style="color: #DE3431">Server Edition</span> AI Workstation',
    subtitle: "Introducing Enthoo Pro 2",
    description: "Built for serious AI workloads. Deep learning, data processing, local model hosting, and everything in between.",
    buttons: [
      { text: "Build Your Own", href: "https://arsenalpc.com/product/custom-ai-workstations/enthoo-pro-2-server-edition-custom-ai-workstation/" }
    ]
  },
  {
    id: 3,
    image: explosivePerformance,
    bgPosition: "center center",
    textBoundary: 35, // Percentage from left where text should stop
    title: 'Custom Gaming PCs with <span style="color:#76B900">RTX</span> & <br> <span style="color: #DE3431">Ryzen</span> Power',
    subtitle: "Premium Components. Ships Fast.",
    description: "Design a custom gaming PC with NVIDIA 50-Series graphics cards and AMD Ryzen CPUs.\nPremium performance for competitive gaming, streaming, and content creation",
    buttons: [
      { text: "Custom PC Builder", href: "/custom-pc-builder/" },
      { text: "Prebuilds", href: "/shop/prebuilds/" }
    ]
  },
  {
    id: 4,
    image: ap202Hero,
    bgPosition: "center bottom", // Move all the way down
    textBoundary: 50, // Percentage from left where text should stop
    title: 'ASUS Prime <span style="color: #DE3431">AP202</span> Builds',
    subtitle: "Black or White Edition",
    description: "A panoramic Micro ATX showcase with curved tempered glass and GPU focused airflow. Built with 360mm radiator support and clean cable routing.",
    buttons: [
      { text: "Build Your Own", href: "https://arsenalpc.com/product/custom-pc/asus-prime-ap202-custom-gaming-pc/" }
    ]
  },
  {
    id: 5,
    image: heroCommunity,
    bgPosition: "center center",
    textBoundary: 40, // Percentage from left where text should stop
    title: 'Go <span style="color: #DE3431">Beyond</span> The Build',
    subtitle: "Join the Enthusiast Community",
    description: "There is more to us than just hardware. Dive into our latest video showcases, read expert guides and reviews, and jump into our new Discord server to chat directly with the team!",
    buttons: [
      { text: "Community", href: "https://arsenalpc.com/community/" },
      { text: "Our New Discord", href: "https://discord.gg/wBvqaSZj", target: "_blank" }
    ]
  },
  {
    id: 6,
    image: amdRyzenHero,
    bgPosition: "center center",
    textBoundary: 35, // Percentage from left where text should stop
    title: 'Level Up With <span style="color: #DE3431">AMD Ryzen</span> Processors',
    subtitle: "Built for Maximum Frames and Multitasking.",
    description: "Experience peak gaming performance with Ryzen processors.\nThese CPUs deliver ultra-high clock speeds, massive core counts, and 3D V-Cache technology for smoother gameplay and faster rendering.",
    buttons: [
      { text: "AMD Builds", href: "/product-brand/amd/" },
      { text: "Gaming PCs", href: "/shop/prebuilds/" }
    ]
  },
  {
    id: 7,
    image: nvidia5090Hero,
    bgPosition: "center center",
    textBoundary: 40, // Percentage from left where text should stop
    title: 'Arm Yourself with <span style="color:#76B900">NVIDIA RTX</span> Power',
    subtitle: "Experience Gaming with 50-Series GPUs",
    description: "Step into the future with RTX 5090, 5080, and 5070 Ti PC builds engineered for speed.\nCustom PCs deliver ray-traced graphics, AI-enhanced performance, and competitive gameplay for creators and pros.",
    buttons: [
      { text: "NVIDIA Builds", href: "/product-brand/nvidia/" },
      { text: "Deals", href: "/special-deals/" }
    ]
  }
];

export default function App() {
  const [currentSlide, setCurrentSlide] = useState(() => {
    // Get saved slide from localStorage, default to 0
    const saved = localStorage.getItem('apc-slider-current-slide');
    return saved ? parseInt(saved) : 0;
  });
  const [isPaused, setIsPaused] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const intervalRef = useRef(null);
  const sliderRef = useRef(null);

  // Dev controls state
  const [showDevControls, setShowDevControls] = useState(true);
  const [currentBreakpoint, setCurrentBreakpoint] = useState('desktop');
  
  // Load saved settings from localStorage or use defaults - now per slide
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('apc-slider-settings');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Check if it's the old format (single settings for all slides)
        if (parsed.desktop && !Array.isArray(parsed)) {
          // Convert old format to new per-slide format
          return slides.map(() => ({
            desktop: parsed.desktop,
            tablet: parsed.tablet,
            mobile: parsed.mobile
          }));
        }
        return parsed;
      } catch (e) {
        console.error('Failed to load saved settings:', e);
      }
    }
    // Initialize per-slide settings
    return slides.map(() => ({
      desktop: { bgPosX: 50, bgPosY: 50, bgScale: 'custom', customScale: 100, titleMaxWidth: 480, subtitleMaxWidth: 480, descMaxWidth: 480, textBoundary: 40 },
      tablet: { bgPosX: 50, bgPosY: 50, bgScale: 'custom', customScale: 100, titleMaxWidth: 400, subtitleMaxWidth: 400, descMaxWidth: 400, textBoundary: 45 },
      mobile: { bgPosX: 50, bgPosY: 50, bgScale: 'custom', customScale: 100, titleMaxWidth: 320, subtitleMaxWidth: 320, descMaxWidth: 320, textBoundary: 50 }
    }));
  });
  
  const [mobileTextBelow, setMobileTextBelow] = useState(() => {
    const saved = localStorage.getItem('apc-slider-mobile-text-below');
    return saved ? saved === 'true' : false;
  });

  // Custom breakpoint values
  const [breakpoints, setBreakpoints] = useState(() => {
    const saved = localStorage.getItem('apc-slider-breakpoints');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to load saved breakpoints:', e);
      }
    }
    return {
      desktop: 1024, // Above this = desktop
      tablet: 768   // Between tablet and desktop = tablet, below tablet = mobile
    };
  });
  
  // Text content editing
  const [editableSlides, setEditableSlides] = useState(() => {
    const saved = localStorage.getItem('apc-slider-editable-slides');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Add textBoundary to old saved slides if missing
        return parsed.map((slide, idx) => ({
          ...slide,
          textBoundary: slide.textBoundary || slides[idx].textBoundary
        }));
      } catch (e) {
        console.error('Failed to load saved slides:', e);
      }
    }
    return slides;
  });
  
  // State to show/hide boundary line in dev mode
  const [showBoundaryLine, setShowBoundaryLine] = useState(false);
  
  const [textStyles, setTextStyles] = useState(() => {
    const saved = localStorage.getItem('apc-slider-text-styles');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to load saved text styles:', e);
      }
    }
    return {
      title: { fontSize: 38, fontWeight: 700 },
      subtitle: { fontSize: 18, fontWeight: 500 },
      description: { fontSize: 14, fontWeight: 400 }
    };
  });

  // Auto-save all settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('apc-slider-current-slide', currentSlide.toString());
  }, [currentSlide]);
  
  useEffect(() => {
    localStorage.setItem('apc-slider-settings', JSON.stringify(settings));
  }, [settings]);
  
  useEffect(() => {
    localStorage.setItem('apc-slider-breakpoints', JSON.stringify(breakpoints));
  }, [breakpoints]);
  
  useEffect(() => {
    localStorage.setItem('apc-slider-mobile-text-below', mobileTextBelow.toString());
  }, [mobileTextBelow]);
  
  useEffect(() => {
    localStorage.setItem('apc-slider-editable-slides', JSON.stringify(editableSlides));
  }, [editableSlides]);
  
  useEffect(() => {
    localStorage.setItem('apc-slider-text-styles', JSON.stringify(textStyles));
  }, [textStyles]);

  // Check breakpoints
  useEffect(() => {
    const checkBreakpoint = () => {
      const width = window.innerWidth;
      setIsMobile(width <= breakpoints.tablet);
      
      if (width <= breakpoints.tablet) {
        setCurrentBreakpoint('mobile');
      } else if (width <= breakpoints.desktop) {
        setCurrentBreakpoint('tablet');
      } else {
        setCurrentBreakpoint('desktop');
      }
    };
    checkBreakpoint();
    window.addEventListener('resize', checkBreakpoint);
    return () => window.removeEventListener('resize', checkBreakpoint);
  }, [breakpoints]);


  // Auto-advance slides - DISABLED
  // useEffect(() => {
  //   if (!isPaused) {
  //     intervalRef.current = setInterval(() => {
  //       setCurrentSlide((prev) => (prev + 1) % slides.length);
  //     }, 5000);
  //   }
  //   return () => {
  //     if (intervalRef.current) clearInterval(intervalRef.current);
  //   };
  // }, [isPaused]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    // Reset interval
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 5000);
    }
  };

  const nextSlide = () => {
    goToSlide((currentSlide + 1) % slides.length);
  };

  const prevSlide = () => {
    goToSlide((currentSlide - 1 + slides.length) % slides.length);
  };

  const currentSlideData = editableSlides[currentSlide];

  return (
    <>
    <div 
      className="apc-hero-slider"
      ref={sliderRef}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlideData.id}
          className={`apc__slide ${isMobile && !mobileTextBelow ? 'apc__slide--panel' : ''}`}
        >
          {/* Background Image */}
          <div 
            className="apc__bg"
            style={{ 
              backgroundImage: `url(${currentSlideData.image})`,
              backgroundPosition: `${settings[currentSlide][currentBreakpoint].bgPosX}% ${settings[currentSlide][currentBreakpoint].bgPosY}%`,
              backgroundSize: settings[currentSlide][currentBreakpoint].bgScale === 'custom' 
                ? `${settings[currentSlide][currentBreakpoint].customScale}%` 
                : settings[currentSlide][currentBreakpoint].bgScale,
              backgroundRepeat: 'no-repeat'
            }}
          />
          
          {/* Text Boundary Line (dev mode only) */}
          {showDevControls && showBoundaryLine && (
            <div
              style={{
                position: 'absolute',
                left: `${settings[currentSlide][currentBreakpoint].textBoundary}%`,
                top: 0,
                bottom: 0,
                width: '2px',
                background: 'rgba(255, 0, 0, 0.5)',
                zIndex: 10,
                pointerEvents: 'none'
              }}
            >
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%) rotate(-90deg)',
                background: 'rgba(255, 0, 0, 0.7)',
                color: 'white',
                padding: '2px 8px',
                fontSize: '10px',
                whiteSpace: 'nowrap'
              }}>
                Text Boundary {settings[currentSlide][currentBreakpoint].textBoundary}%
              </div>
            </div>
          )}

          {/* Mobile Overlay (only visible on mobile panel mode) */}
          {isMobile && !mobileTextBelow && (
            <div className="apc__overlay">
              <h2 
                className="apc__title"
                dangerouslySetInnerHTML={{ __html: currentSlideData.title }}
                style={{ 
                  fontSize: `${textStyles.title.fontSize * 0.6}px`,
                  fontWeight: textStyles.title.fontWeight
                }}
              />
              <div 
                className="apc__subtitle"
                style={{ 
                  fontSize: `${textStyles.subtitle.fontSize * 0.9}px`,
                  fontWeight: textStyles.subtitle.fontWeight
                }}
              >
                {currentSlideData.subtitle}
              </div>
            </div>
          )}

          {/* Main Content */}
          <div 
            className="apc__inner apc__inner--left"
            style={{
              maxWidth: `${settings[currentSlide][currentBreakpoint].textBoundary}%`,
              width: '100%'
            }}
          >
            <h2 
              className="apc__title"
              dangerouslySetInnerHTML={{ __html: currentSlideData.title }}
              style={{ 
                fontSize: `${textStyles.title.fontSize}px`,
                fontWeight: textStyles.title.fontWeight,
                maxWidth: `${settings[currentSlide][currentBreakpoint].titleMaxWidth}px`
              }}
            />
            <div 
              className="apc__subtitle"
              style={{ 
                fontSize: `${textStyles.subtitle.fontSize}px`,
                fontWeight: textStyles.subtitle.fontWeight,
                maxWidth: `${settings[currentSlide][currentBreakpoint].subtitleMaxWidth}px`
              }}
            >
              {currentSlideData.subtitle}
            </div>
            <div 
              className="apc__desc"
              style={{ 
                fontSize: `${textStyles.description.fontSize}px`,
                fontWeight: textStyles.description.fontWeight,
                maxWidth: `${settings[currentSlide][currentBreakpoint].descMaxWidth}px`
              }}
            >
              {currentSlideData.description.split('\n').map((line, i) => (
                <span key={i}>
                  {line}
                  {i < currentSlideData.description.split('\n').length - 1 && <br />}
                </span>
              ))}
            </div>
            <div className="apc__buttons">
              {currentSlideData.buttons.map((button, index) => (
                <a 
                  key={index}
                  className="elementor-button"
                  href={button.href}
                  target={button.target || "_self"}
                  rel={button.target === "_blank" ? "noopener" : undefined}
                >
                  <span className="elementor-button-text">{button.text}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Mobile Panel (only visible on mobile panel mode) */}
          {isMobile && !mobileTextBelow && (
            <div className="apc__mobile-panel">
              <div className="apc__inner apc__inner--left">
                <div className="apc__desc">
                  {currentSlideData.description.split('\n').map((line, i) => (
                    <span key={i}>
                      {line}
                      {i < currentSlideData.description.split('\n').length - 1 && <br />}
                    </span>
                  ))}
                </div>
                <div className="apc__buttons">
                  {currentSlideData.buttons.map((button, index) => (
                    <a 
                      key={index}
                      className="elementor-button"
                      href={button.href}
                      target={button.target || "_self"}
                      rel={button.target === "_blank" ? "noopener" : undefined}
                    >
                      <span className="elementor-button-text">{button.text}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Mobile Text Below (dark area below image) */}
      {isMobile && mobileTextBelow && (
        <div style={{
          backgroundColor: '#000',
          padding: '20px',
          color: 'white'
        }}>
          <div style={{ margin: '0 auto' }}>
            <h2 
              className="apc__title"
              dangerouslySetInnerHTML={{ __html: currentSlideData.title }}
              style={{ 
                fontSize: `${textStyles.title.fontSize * 0.7}px`, 
                marginBottom: '10px',
                fontWeight: textStyles.title.fontWeight,
                maxWidth: `${settings[currentSlide].mobile.titleMaxWidth}px`
              }}
            />
            <div 
              className="apc__subtitle" 
              style={{ 
                fontSize: `${textStyles.subtitle.fontSize}px`, 
                marginBottom: '15px', 
                opacity: 0.9,
                fontWeight: textStyles.subtitle.fontWeight,
                maxWidth: `${settings[currentSlide].mobile.subtitleMaxWidth}px`
              }}
            >
              {currentSlideData.subtitle}
            </div>
            <div 
              className="apc__desc" 
              style={{ 
                fontSize: `${textStyles.description.fontSize}px`, 
                lineHeight: '1.5', 
                marginBottom: '20px', 
                opacity: 0.85,
                fontWeight: textStyles.description.fontWeight,
                maxWidth: `${settings[currentSlide].mobile.descMaxWidth}px`
              }}
            >
              {currentSlideData.description.split('\n').map((line, i) => (
                <span key={i}>
                  {line}
                  {i < currentSlideData.description.split('\n').length - 1 && <br />}
                </span>
              ))}
            </div>
            <div className="apc__buttons">
              {currentSlideData.buttons.map((button, index) => (
                <a 
                  key={index}
                  className="elementor-button"
                  href={button.href}
                  target={button.target || "_self"}
                  rel={button.target === "_blank" ? "noopener" : undefined}
                >
                  <span className="elementor-button-text">{button.text}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Navigation Arrows */}
      <button 
        className="apc__nav apc__nav--prev"
        onClick={prevSlide}
        aria-label="Previous slide"
      />
      <button 
        className="apc__nav apc__nav--next"
        onClick={nextSlide}
        aria-label="Next slide"
      />

      {/* Pagination Dots */}
      <div className="swiper-pagination">
        {slides.map((_, index) => (
          <span
            key={index}
            className={`swiper-pagination-bullet ${index === currentSlide ? 'swiper-pagination-bullet-active' : ''}`}
            onClick={() => goToSlide(index)}
            role="button"
            aria-label={`Go to slide ${index + 1}`}
            tabIndex={0}
          />
        ))}
      </div>
    </div>

    {/* Dev Controls */}
    {showDevControls && (
      <div style={{
        marginTop: '20px',
        padding: '20px',
        backgroundColor: '#1a1a1a',
        borderRadius: '8px',
        color: 'white',
        fontFamily: 'monospace'
      }}>
        <h3 style={{ margin: '0 0 15px 0', color: '#DE3431' }}>
          Dev Controls - Slide {currentSlide + 1}: {editableSlides[currentSlide].title.replace(/<[^>]*>/g, '')}
        </h3>
        
        {/* Breakpoint Tabs */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{ marginRight: '10px', color: '#ccc' }}>Current Breakpoint: </label>
          {['desktop', 'tablet', 'mobile'].map(bp => (
            <button
              key={bp}
              onClick={() => setCurrentBreakpoint(bp)}
              style={{
                padding: '6px 12px',
                marginRight: '5px',
                backgroundColor: bp === currentBreakpoint ? '#DE3431' : '#333',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                textTransform: 'capitalize'
              }}
            >
              {bp} {bp === 'desktop' && `(>${breakpoints.desktop}px)`} {bp === 'tablet' && `(${breakpoints.tablet}-${breakpoints.desktop}px)`} {bp === 'mobile' && `(≤${breakpoints.tablet-1}px)`}
            </button>
          ))}
        </div>

        {/* Breakpoint Value Controls */}
        <div style={{ marginBottom: '20px', paddingBottom: '15px', borderBottom: '1px solid #333' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#DE3431', fontSize: '14px' }}>Custom Breakpoint Values:</h4>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', color: '#ccc', fontSize: '12px' }}>
                Desktop Breakpoint: {breakpoints.desktop}px (above this = desktop)
              </label>
              <input 
                type="range" 
                min="800" 
                max="1400" 
                step="50"
                value={breakpoints.desktop}
                onChange={(e) => {
                  setBreakpoints(prev => ({ ...prev, desktop: parseInt(e.target.value) }));
                }}
                style={{ width: '150px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', color: '#ccc', fontSize: '12px' }}>
                Tablet Breakpoint: {breakpoints.tablet}px (above this = tablet)
              </label>
              <input 
                type="range" 
                min="480" 
                max="900" 
                step="24"
                value={breakpoints.tablet}
                onChange={(e) => {
                  setBreakpoints(prev => ({ ...prev, tablet: parseInt(e.target.value) }));
                }}
                style={{ width: '150px' }}
              />
            </div>
          </div>
          <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: '#666' }}>
            Mobile = below {breakpoints.tablet}px • Tablet = {breakpoints.tablet}px to {breakpoints.desktop}px • Desktop = above {breakpoints.desktop}px
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button 
            onClick={() => setIsPaused(!isPaused)}
            style={{
              padding: '8px 16px',
              backgroundColor: isPaused ? '#DE3431' : '#333',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {isPaused ? 'Resume Auto-slide' : 'Pause Auto-slide'}
          </button>

          <div>
            <label>X Position: {settings[currentSlide][currentBreakpoint].bgPosX}%</label>
            <input 
              type="range" 
              min="-50" 
              max="150" 
              value={settings[currentSlide][currentBreakpoint].bgPosX}
              onChange={(e) => {
                const newValue = parseInt(e.target.value);
                setSettings(prev => {
                  const newSettings = [...prev];
                  newSettings[currentSlide] = {
                    ...newSettings[currentSlide],
                    [currentBreakpoint]: { ...newSettings[currentSlide][currentBreakpoint], bgPosX: newValue }
                  };
                  return newSettings;
                });
              }}
              style={{ marginLeft: '10px' }}
            />
          </div>

          <div>
            <label>Y Position: {settings[currentSlide][currentBreakpoint].bgPosY}%</label>
            <input 
              type="range" 
              min="-50" 
              max="150" 
              value={settings[currentSlide][currentBreakpoint].bgPosY}
              onChange={(e) => {
                const newValue = parseInt(e.target.value);
                setSettings(prev => {
                  const newSettings = [...prev];
                  newSettings[currentSlide] = {
                    ...newSettings[currentSlide],
                    [currentBreakpoint]: { ...newSettings[currentSlide][currentBreakpoint], bgPosY: newValue }
                  };
                  return newSettings;
                });
              }}
              style={{ marginLeft: '10px' }}
            />
          </div>

          <div>
            <label>Scale: </label>
            <select 
              value={settings[currentSlide][currentBreakpoint].bgScale}
              onChange={(e) => {
                const newValue = e.target.value;
                setSettings(prev => {
                  const newSettings = [...prev];
                  newSettings[currentSlide] = {
                    ...newSettings[currentSlide],
                    [currentBreakpoint]: { ...newSettings[currentSlide][currentBreakpoint], bgScale: newValue }
                  };
                  return newSettings;
                });
              }}
              style={{
                marginLeft: '10px',
                padding: '4px',
                backgroundColor: '#333',
                color: 'white',
                border: '1px solid #555'
              }}
            >
              <option value="cover">Cover (default)</option>
              <option value="contain">Contain (fit inside)</option>
              <option value="auto">Auto (original size)</option>
              <option value="50%">50%</option>
              <option value="75%">75%</option>
              <option value="90%">90%</option>
              <option value="110%">110%</option>
              <option value="125%">125%</option>
              <option value="150%">150%</option>
              <option value="custom">Custom %</option>
            </select>
          </div>

          {settings[currentSlide][currentBreakpoint].bgScale === 'custom' && (
            <div>
              <label>Custom Scale: {settings[currentSlide][currentBreakpoint].customScale}%</label>
              <input 
                type="range" 
                min="30" 
                max="200" 
                step="1"
                value={settings[currentSlide][currentBreakpoint].customScale}
                onChange={(e) => {
                  const newValue = parseInt(e.target.value);
                  setSettings(prev => {
                    const newSettings = [...prev];
                    newSettings[currentSlide] = {
                      ...newSettings[currentSlide],
                      [currentBreakpoint]: { ...newSettings[currentSlide][currentBreakpoint], customScale: newValue }
                    };
                    return newSettings;
                  });
                }}
                style={{ marginLeft: '10px' }}
              />
            </div>
          )}

          <div>
            <label>Title Max Width: {settings[currentSlide][currentBreakpoint].titleMaxWidth}px</label>
            <input 
              type="range" 
              min="200" 
              max="600" 
              step="20"
              value={settings[currentSlide][currentBreakpoint].titleMaxWidth}
              onChange={(e) => {
                const newValue = parseInt(e.target.value);
                setSettings(prev => {
                  const newSettings = [...prev];
                  newSettings[currentSlide] = {
                    ...newSettings[currentSlide],
                    [currentBreakpoint]: { ...newSettings[currentSlide][currentBreakpoint], titleMaxWidth: newValue }
                  };
                  return newSettings;
                });
              }}
              style={{ marginLeft: '10px' }}
            />
          </div>

          <div>
            <label>Subtitle Max Width: {settings[currentSlide][currentBreakpoint].subtitleMaxWidth}px</label>
            <input 
              type="range" 
              min="200" 
              max="600" 
              step="20"
              value={settings[currentSlide][currentBreakpoint].subtitleMaxWidth}
              onChange={(e) => {
                const newValue = parseInt(e.target.value);
                setSettings(prev => {
                  const newSettings = [...prev];
                  newSettings[currentSlide] = {
                    ...newSettings[currentSlide],
                    [currentBreakpoint]: { ...newSettings[currentSlide][currentBreakpoint], subtitleMaxWidth: newValue }
                  };
                  return newSettings;
                });
              }}
              style={{ marginLeft: '10px' }}
            />
          </div>

          <div>
            <label>Description Max Width: {settings[currentSlide][currentBreakpoint].descMaxWidth}px</label>
            <input 
              type="range" 
              min="200" 
              max="600" 
              step="20"
              value={settings[currentSlide][currentBreakpoint].descMaxWidth}
              onChange={(e) => {
                const newValue = parseInt(e.target.value);
                setSettings(prev => {
                  const newSettings = [...prev];
                  newSettings[currentSlide] = {
                    ...newSettings[currentSlide],
                    [currentBreakpoint]: { ...newSettings[currentSlide][currentBreakpoint], descMaxWidth: newValue }
                  };
                  return newSettings;
                });
              }}
              style={{ marginLeft: '10px' }}
            />
          </div>

          <div>
            <label>Text Boundary: {settings[currentSlide][currentBreakpoint].textBoundary}%</label>
            <input 
              type="range" 
              min="20" 
              max="80" 
              step="5"
              value={settings[currentSlide][currentBreakpoint].textBoundary}
              onChange={(e) => {
                const newValue = parseInt(e.target.value);
                setSettings(prev => {
                  const newSettings = [...prev];
                  newSettings[currentSlide] = {
                    ...newSettings[currentSlide],
                    [currentBreakpoint]: { ...newSettings[currentSlide][currentBreakpoint], textBoundary: newValue }
                  };
                  return newSettings;
                });
              }}
              style={{ marginLeft: '10px' }}
            />
          </div>

          <div>
            <label style={{ marginRight: '10px' }}>
              <input 
                type="checkbox" 
                checked={showBoundaryLine}
                onChange={(e) => setShowBoundaryLine(e.target.checked)}
                style={{ marginRight: '5px' }}
              />
              Show Boundary Line
            </label>
          </div>

          {isMobile && (
            <div>
              <label style={{ marginRight: '10px' }}>Mobile Text Position:</label>
              <label style={{ marginRight: '15px' }}>
                <input 
                  type="radio" 
                  name="mobileTextPos" 
                  checked={!mobileTextBelow}
                  onChange={() => setMobileTextBelow(false)}
                  style={{ marginRight: '5px' }}
                />
                Overlay on Image
              </label>
              <label>
                <input 
                  type="radio" 
                  name="mobileTextPos" 
                  checked={mobileTextBelow}
                  onChange={() => setMobileTextBelow(true)}
                  style={{ marginRight: '5px' }}
                />
                Below Image (Dark Area)
              </label>
            </div>
          )}

          <button 
            onClick={() => {
              if (confirm('Reset all settings to defaults? This will clear all saved settings.')) {
                // Clear all saved settings
                localStorage.removeItem('apc-slider-settings');
                localStorage.removeItem('apc-slider-breakpoints');
                localStorage.removeItem('apc-slider-mobile-text-below');
                localStorage.removeItem('apc-slider-editable-slides');
                localStorage.removeItem('apc-slider-text-styles');
                localStorage.removeItem('apc-slider-current-slide');
                
                // Reset to defaults
                setSettings(slides.map(() => ({
                  desktop: { bgPosX: 50, bgPosY: 50, bgScale: 'custom', customScale: 100, titleMaxWidth: 480, subtitleMaxWidth: 480, descMaxWidth: 480, textBoundary: 40 },
                  tablet: { bgPosX: 50, bgPosY: 50, bgScale: 'custom', customScale: 100, titleMaxWidth: 400, subtitleMaxWidth: 400, descMaxWidth: 400, textBoundary: 45 },
                  mobile: { bgPosX: 50, bgPosY: 50, bgScale: 'custom', customScale: 100, titleMaxWidth: 320, subtitleMaxWidth: 320, descMaxWidth: 320, textBoundary: 50 }
                })));
                setBreakpoints({ desktop: 1024, tablet: 768 });
                setMobileTextBelow(false);
                setEditableSlides(slides);
                setTextStyles({
                  title: { fontSize: 38, fontWeight: 700 },
                  subtitle: { fontSize: 18, fontWeight: 500 },
                  description: { fontSize: 14, fontWeight: 400 }
                });
                setCurrentSlide(0);
              }
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Reset All Settings
          </button>
          
          <div style={{ 
            padding: '4px 8px', 
            backgroundColor: '#22c55e', 
            color: 'white', 
            borderRadius: '4px',
            fontSize: '12px',
            display: 'inline-block'
          }}>
            ✓ Auto-saving
          </div>
        </div>

        {/* Text Content Editing */}
        <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #333' }}>
          <h4 style={{ margin: '0 0 15px 0', color: '#DE3431' }}>Text Editing</h4>
          
          <div style={{ display: 'grid', gap: '15px', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', color: '#ccc' }}>Title:</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginBottom: '8px' }}>
                <button
                  onClick={() => {
                    const newSlides = [...editableSlides];
                    newSlides[currentSlide] = { ...newSlides[currentSlide], title: slides[currentSlide].title };
                    setEditableSlides(newSlides);
                  }}
                  style={{
                    padding: '4px 8px',
                    backgroundColor: '#555',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                  title="Reset to original with HTML styling"
                >
                  Reset HTML
                </button>
                <span style={{ fontSize: '12px', color: '#888' }}>
                  {currentSlideData.title.includes('<') ? '(Has HTML styling)' : '(Plain text)'}
                </span>
              </div>
              <textarea
                value={currentSlideData.title}
                onChange={(e) => {
                  const newSlides = [...editableSlides];
                  newSlides[currentSlide] = { ...newSlides[currentSlide], title: e.target.value };
                  setEditableSlides(newSlides);
                }}
                style={{
                  width: '100%',
                  height: '60px',
                  padding: '8px',
                  backgroundColor: '#333',
                  color: 'white',
                  border: '1px solid #555',
                  borderRadius: '4px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', color: '#ccc' }}>Subtitle:</label>
              <input
                type="text"
                value={currentSlideData.subtitle}
                onChange={(e) => {
                  const newSlides = [...editableSlides];
                  newSlides[currentSlide] = { ...newSlides[currentSlide], subtitle: e.target.value };
                  setEditableSlides(newSlides);
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: '#333',
                  color: 'white',
                  border: '1px solid #555',
                  borderRadius: '4px'
                }}
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: '#ccc' }}>Description:</label>
              <textarea
                value={currentSlideData.description}
                onChange={(e) => {
                  const newSlides = [...editableSlides];
                  newSlides[currentSlide] = { ...newSlides[currentSlide], description: e.target.value };
                  setEditableSlides(newSlides);
                }}
                style={{
                  width: '100%',
                  height: '80px',
                  padding: '8px',
                  backgroundColor: '#333',
                  color: 'white',
                  border: '1px solid #555',
                  borderRadius: '4px',
                  resize: 'vertical'
                }}
              />
            </div>
          </div>

          {/* Text Styling Controls */}
          <div style={{ marginTop: '15px' }}>
            <h5 style={{ margin: '0 0 10px 0', color: '#ccc' }}>Text Styling</h5>
            <div style={{ display: 'grid', gap: '15px', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
              
              {/* Title Styling */}
              <div>
                <label style={{ color: '#ccc', fontSize: '12px' }}>Title Size: {textStyles.title.fontSize}px</label>
                <input
                  type="range"
                  min="20"
                  max="60"
                  value={textStyles.title.fontSize}
                  onChange={(e) => setTextStyles(prev => ({
                    ...prev,
                    title: { ...prev.title, fontSize: parseInt(e.target.value) }
                  }))}
                  style={{ width: '100%', marginTop: '5px' }}
                />
                <label style={{ color: '#ccc', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                  Title Weight: {textStyles.title.fontWeight}
                </label>
                <input
                  type="range"
                  min="100"
                  max="900"
                  step="100"
                  value={textStyles.title.fontWeight}
                  onChange={(e) => setTextStyles(prev => ({
                    ...prev,
                    title: { ...prev.title, fontWeight: parseInt(e.target.value) }
                  }))}
                  style={{ width: '100%', marginTop: '5px' }}
                />
              </div>

              {/* Subtitle Styling */}
              <div>
                <label style={{ color: '#ccc', fontSize: '12px' }}>Subtitle Size: {textStyles.subtitle.fontSize}px</label>
                <input
                  type="range"
                  min="12"
                  max="30"
                  value={textStyles.subtitle.fontSize}
                  onChange={(e) => setTextStyles(prev => ({
                    ...prev,
                    subtitle: { ...prev.subtitle, fontSize: parseInt(e.target.value) }
                  }))}
                  style={{ width: '100%', marginTop: '5px' }}
                />
                <label style={{ color: '#ccc', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                  Subtitle Weight: {textStyles.subtitle.fontWeight}
                </label>
                <input
                  type="range"
                  min="100"
                  max="900"
                  step="100"
                  value={textStyles.subtitle.fontWeight}
                  onChange={(e) => setTextStyles(prev => ({
                    ...prev,
                    subtitle: { ...prev.subtitle, fontWeight: parseInt(e.target.value) }
                  }))}
                  style={{ width: '100%', marginTop: '5px' }}
                />
              </div>

              {/* Description Styling */}
              <div>
                <label style={{ color: '#ccc', fontSize: '12px' }}>Description Size: {textStyles.description.fontSize}px</label>
                <input
                  type="range"
                  min="10"
                  max="24"
                  value={textStyles.description.fontSize}
                  onChange={(e) => setTextStyles(prev => ({
                    ...prev,
                    description: { ...prev.description, fontSize: parseInt(e.target.value) }
                  }))}
                  style={{ width: '100%', marginTop: '5px' }}
                />
                <label style={{ color: '#ccc', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                  Description Weight: {textStyles.description.fontWeight}
                </label>
                <input
                  type="range"
                  min="100"
                  max="900"
                  step="100"
                  value={textStyles.description.fontWeight}
                  onChange={(e) => setTextStyles(prev => ({
                    ...prev,
                    description: { ...prev.description, fontWeight: parseInt(e.target.value) }
                  }))}
                  style={{ width: '100%', marginTop: '5px' }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    )}
  </>
  );
}